import type { MarketId } from './LockedState';

const ALL: MarketId[] = ['spot', 'margin', 'futures'];
const LABELS: Record<MarketId, string> = {
  spot: 'Spot',
  margin: 'Margin',
  futures: 'Futures',
};

type Props = {
  value: MarketId;
  onChange: (m: MarketId) => void;
  className?: string;
  /**
   * Markets that have no active symbols today. Shown visually dimmed
   * but still selectable — the page behind honours the selection with
   * an empty-state panel.
   */
  emptyMarkets?: MarketId[];
};

export function MarketSelector({
  value,
  onChange,
  className = '',
  emptyMarkets,
}: Props) {
  const isEmpty = (m: MarketId) => emptyMarkets?.includes(m) ?? false;
  return (
    <div
      role="tablist"
      aria-label="Market selector"
      className={`inline-flex items-center rounded border border-border bg-bg-tertiary p-0.5 text-[11px] ${className}`}
    >
      {ALL.map((m) => {
        const active = m === value;
        const dim = !active && isEmpty(m);
        return (
          <button
            key={m}
            role="tab"
            aria-selected={active}
            type="button"
            onClick={() => onChange(m)}
            className={`relative px-2.5 py-1 rounded font-medium transition-colors ${
              active
                ? 'bg-accent text-white'
                : `text-text-secondary hover:text-text-primary hover:bg-bg-secondary ${dim ? 'opacity-50' : ''}`
            }`}
            title={dim ? `No active ${m} symbols yet` : `Switch to ${LABELS[m]}`}
          >
            {LABELS[m]}
          </button>
        );
      })}
    </div>
  );
}
